// Tag claim rules. Words: TAG_CLAIM_WORDS.md (approved 2026-10-06).
// Shared by the claim page and the claimTag server step, and unit tested.
// The flow is the live seven-step claim. Only the LOTO step's rules are new:
// the worker reports a date, someone else verifies it (QEW_DASHBOARD_WORDS.md).
// Nothing here reads or writes the database.

import { CLAIM_DEFAULT_AUTHORITIES } from "./badge";

export const DEPARTMENTS = ["Maintenance", "Engineering", "Facilities", "Other"] as const;
export const ROLES = ["General Maintenance", "Facilities Maintenance", "Electrical Tech", "Engineer", "Electrician", "Other"] as const;
export const EXPERIENCE = ["Less than 1 year", "1–3 years", "4–7 years", "8–15 years", "15+ years"] as const;

/** LOTO refresher every 3 years, reminder at 1 year (NFPA 70E, Robert 2026-10-06). */
export const LOTO_VALID_YEARS = 3;
export const LOTO_REMINDER_YEARS = 1;

export interface ClaimInput {
  tagId: string;
  sessionCode: string;
  firstName: string;
  lastName: string;
  department: string;
  role: string;
  yearsExperience: string;
  /** YYYY-MM-DD, or "" when the box is ticked. */
  lotoDate: string;
  lotoSkipped: boolean;
  /** Optional. Only New Trainee asks for these today. */
  phone?: string;
  email?: string;
}

export type LotoCheck =
  | { kind: "empty" }
  | { kind: "skipped" }
  | { kind: "future" }
  | { kind: "current"; expiresOn: string }
  | { kind: "overOneYear"; expiresOn: string }
  | { kind: "expired"; expiresOn: string };

/** YYYY-MM-DD plus whole years. 29 February lands on 28 February. */
export function addYears(iso: string, years: number): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const ty = y + years;
  const last = new Date(Date.UTC(ty, m, 0)).getUTCDate();
  return `${String(ty).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

function isIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** The LOTO step's answer for a date, against today in site time. */
export function lotoCheck(lotoDate: string, skipped: boolean, today: string): LotoCheck {
  if (skipped) return { kind: "skipped" };
  const d = lotoDate.trim();
  if (!d || !isIsoDate(d)) return { kind: "empty" };
  if (d > today) return { kind: "future" };
  const expiresOn = addYears(d, LOTO_VALID_YEARS);
  if (expiresOn < today) return { kind: "expired", expiresOn };
  if (addYears(d, LOTO_REMINDER_YEARS) < today) return { kind: "overOneYear", expiresOn };
  return { kind: "current", expiresOn };
}

/** 2029-05-05 -> 5/5/2029, the way the live claim prints dates. */
export function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${m}/${d}/${y}`;
}

/** The line under the LOTO date, and its tone. Approved wording, TAG_CLAIM_WORDS.md. */
export function lotoLine(check: LotoCheck): { tone: "ok" | "amber" | "red" | "bad"; text: string } | null {
  switch (check.kind) {
    case "current":
      return { tone: "ok", text: `Sent for verification. Current through ${shortDate(check.expiresOn)}.` };
    case "overOneYear":
      return { tone: "amber", text: "Over 1 year since your LOTO refresher. Sent for verification." };
    case "expired":
      return { tone: "red", text: "LOTO expired. Report your new date when you have it." };
    case "future":
      return { tone: "bad", text: "That date is in the future. Check it before continuing." };
    default:
      return null;
  }
}

/** Every reason a claim is refused before the database is touched. Null when it can go. */
export function claimInputProblem(input: ClaimInput, today: string): string | null {
  if (!/^\d{2}-QEW\d{2}$/.test(input.tagId)) return "This is not a QEW bag tag.";
  if (!/^[A-Z0-9]{4}$/.test(input.sessionCode)) return "Enter the 4-character session code.";
  return traineeInputProblem(input, today);
}

/** The answers both ways in share: the trainee's own claim and New Trainee in the portal. */
export function traineeInputProblem(input: ClaimInput, today: string): string | null {
  if (!/^\d{2}-QEW\d{2}$/.test(input.tagId)) return "This is not a QEW bag tag.";
  if (!input.firstName.trim() || !input.lastName.trim()) return "First and last name are required.";
  if (!(DEPARTMENTS as readonly string[]).includes(input.department)) return "Pick a department.";
  if (!(ROLES as readonly string[]).includes(input.role)) return "Pick a role.";
  if (!(EXPERIENCE as readonly string[]).includes(input.yearsExperience)) return "Pick your electrical experience.";
  const loto = lotoCheck(input.lotoDate, input.lotoSkipped, today);
  if (loto.kind === "empty") return "Enter your LOTO training date, or tick the box.";
  if (loto.kind === "future") return "That LOTO date is in the future. Check it before continuing.";
  if (input.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) return "Check the email address.";
  return null;
}

/** Cleans what the phone sent. Names trimmed and single-spaced, code upper case. */
export function cleanClaimInput(raw: Record<string, unknown>, tagId: string): ClaimInput {
  const s = (v: unknown) => (typeof v === "string" ? v.trim().replace(/\s+/g, " ") : "");
  return {
    tagId,
    sessionCode: s(raw.sessionCode).toUpperCase(),
    firstName: s(raw.firstName),
    lastName: s(raw.lastName),
    department: s(raw.department),
    role: s(raw.role),
    yearsExperience: s(raw.yearsExperience),
    lotoDate: s(raw.lotoDate),
    lotoSkipped: raw.lotoSkipped === true || raw.lotoSkipped === "true",
    phone: s(raw.phone),
    email: s(raw.email).toLowerCase(),
  };
}

/** Fields written onto the badge at claim. Unclaimed becomes Active. Session is null when New Trainee runs with no class open. */
export function badgeClaimFields(input: ClaimInput, today: string, sessionId: string | null) {
  return {
    "Person ID": input.tagId,
    "Holder Name": `${input.firstName} ${input.lastName}`,
    Status: "Active" as const,
    "Issued On": today,
    Authorities: [...CLAIM_DEFAULT_AUTHORITIES],
    claimedOn: today,
    claimedSessionId: sessionId,
  };
}

/**
 * The new person record. Level TRAINEE. LOTO as reported, never verified here.
 * addedBy is set when an admin adds the trainee from the Training Portal (NEW_TRAINEE_WORDS.md).
 */
export function personAtClaim(
  input: ClaimInput,
  today: string,
  sessionId: string | null,
  nowIso: string,
  addedBy: { badgeId: string; name: string } | null = null,
) {
  const loto = lotoCheck(input.lotoDate, input.lotoSkipped, today);
  const reported = loto.kind === "current" || loto.kind === "overOneYear" || loto.kind === "expired";
  return {
    qewId: input.tagId,
    name: `${input.firstName} ${input.lastName}`,
    siteNumber: input.tagId.slice(0, 2),
    department: input.department,
    role: input.role,
    yearsExperience: input.yearsExperience,
    phone: input.phone || null,
    email: input.email || null,
    level: "TRAINEE",
    isActive: false,
    assignedOn: today,
    claimedOn: today,
    claimedSessionId: sessionId,
    lotoTrainedOn: reported ? input.lotoDate : null,
    lotoExpiresOn: reported ? loto.expiresOn : null,
    lotoReportedOn: today,
    lotoReportedBy: input.tagId,
    lotoNoneReported: loto.kind === "skipped",
    lotoVerifiedBy: null,
    lotoVerifiedOn: null,
    lotoSource: addedBy ? "Training Portal" : "Tag claim",
    notes: addedBy
      ? `Added by ${addedBy.name} in the Training Portal${sessionId ? `, session ${sessionId}` : ""}`
      : `Claimed via bag tag scan, session ${sessionId}`,
    addedBy: addedBy?.badgeId ?? null,
    addedByName: addedBy?.name ?? null,
    createdAt: nowIso,
    source: addedBy ? "Training Portal" : "Tag claim",
  };
}

// ---- What a scanned tag shows ----

export type TagView =
  | { kind: "unclaimed"; tagId: string; siteNumber: string; siteName: string }
  | { kind: "claimed"; tagId: string; siteNumber: string; siteName: string; holderName: string }
  | { kind: "disabled"; tagId: string; siteNumber: string; siteName: string }
  | { kind: "notFound"; tagId: string };

/** Reads a badge document into what the tag page shows. */
export function tagViewOf(tagId: string, badge: Record<string, unknown> | null, siteName: string): TagView {
  if (!badge) return { kind: "notFound", tagId };
  const siteNumber = tagId.slice(0, 2);
  const status = String(badge.Status ?? "");
  if (status === "Unclaimed") return { kind: "unclaimed", tagId, siteNumber, siteName };
  if (status === "Disabled") return { kind: "disabled", tagId, siteNumber, siteName };
  return { kind: "claimed", tagId, siteNumber, siteName, holderName: String(badge["Holder Name"] ?? "") };
}

// ---- Session code check (claim step 1) ----

export type SessionCheck =
  | { kind: "open"; sessionId: string; sessionCode: string; siteName: string; startedByName: string }
  | { kind: "otherSite"; sessionCode: string; siteName: string }
  | { kind: "closed"; sessionCode: string }
  | { kind: "notFound"; sessionCode: string };

/** The note under the session code, live wording. */
export function sessionLine(check: SessionCheck): { ok: boolean; text: string } {
  switch (check.kind) {
    case "open":
      return { ok: true, text: `Session ${check.sessionCode} open at ${check.siteName || "this site"}${check.startedByName ? ` · ${check.startedByName}` : ""}` };
    case "otherSite":
      return { ok: false, text: `Session ${check.sessionCode} is open at ${check.siteName || "a different site"}, not this site. Ask your ESP Manager for the local code.` };
    case "closed":
      return { ok: false, text: `Session ${check.sessionCode} is closed. Enrollment needs an open session. Ask your ESP Manager.` };
    default:
      return { ok: false, text: "No open session with that code. Check the training room screen." };
  }
}
